import React, { type JSX } from 'react';
import { Button, Divider, Paper, Stack } from '@mui/material';
import { WidgetFooter, WidgetHeader } from 'pages/WidgetManagement/common';
import ScheduleData from './ScheduleData';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { Schedule, Add } from '@mui/icons-material';
import { CustomTransComponent } from 'components';
import { useNavigate } from 'react-router-dom';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useRBAC } from 'hooks';
import { useTypedSelector } from '@fiji/common';

const getIds = (data: any): any => {
    let ids: any = [];
    data?.forEach((item: any) => {
        ids = [...ids, ...(item?.deviceIds ?? [])];
    });
    return ids
        ?.filter((item: any, index: number, array: any[]) => index === array?.findIndex((t: any) => t?.id === item?.id))
        ?.map((item: any) => ({ ...item, deviceName: item?.name, deviceId: item?.id }));
};

const Component = (props: any): JSX.Element => {
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canViewScheduleDetails = hasPermission('scheduling-details_view-schedules');
    const canViewScheduleDetailsExpired = hasPermission('scheduling-details_view-schedules_hasExpired');
    const canCreateSchedule = hasPermission('scheduling-details_create-schedules');
    const navigate = useNavigate();
    const currentRealmName = useTypedSelector((state: any) => state.common.selectedRealm);
    const currentOrg = useTypedSelector(selectedOrg);
    const selectedNode = useTypedSelector((state: any) => state.common.selectedNode);

    return (
        <Paper sx={{ height: '100%' }}>
            {props?.widgetData?.viewTitleBar && (
                <WidgetHeader
                    mode={props?.mode}
                    widgetData={props?.widgetData}
                    count={
                        props?.widgetData?.multiDevice
                            ? props?.widgetData?.filters?.deviceId?.length
                            : props?.widgetData?.total
                    }
                />
            )}

            {props?.mode !== 'edit' && <Divider />}
            <Stack
                sx={{
                    height: `calc(100% - ${
                        props?.mode === 'edit' || props?.widgetData?.viewAllButton ? '97px' : '49px'
                    })`,
                    overflow: 'auto',
                }}
            >
                {props?.widgetData?.secondary?.length ? (
                    <ScheduleData
                        data={props?.widgetData?.secondary}
                        maxCount={props?.widgetData?.maxCount}
                        mode={props?.mode}
                    />
                ) : (
                    <CustomEmptyState
                        className="padding-y-96"
                        icon={<Schedule fontSize={'inherit'} />}
                        title={<CustomTransComponent translationKey={'SCHEDULING:NO_SCHEDULE_ACTIONS'} />}
                        description={<CustomTransComponent translationKey={'SCHEDULING:ADD_SCHEDULE_ACTIONS'} />}
                        actions={
                            canCreateSchedule ? (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={(): void =>
                                        navigate(
                                            `/${currentRealmName}/scheduleEvent/${selectedNode?.id ?? currentOrg?.id}`
                                        )
                                    }
                                >
                                    <CustomTransComponent translationKey={'SCHEDULING:ADD_SCHEDULE'} />
                                </Button>
                            ) : (
                                <></>
                            )
                        }
                    />
                )}
            </Stack>
            {props?.widgetData?.viewAllButton && (canViewScheduleDetails || canViewScheduleDetailsExpired) && (
                <WidgetFooter
                    mode={props?.mode ?? ''}
                    actionBtnName={<CustomTransComponent translationKey={'COMMON:VIEW_SCHEDULE'} />}
                    widgetData={props?.widgetData}
                    ids={getIds(props?.widgetData?.secondary)}
                    disabled={!props?.widgetData?.secondary?.length}
                />
            )}
        </Paper>
    );
};

export default Component;
